import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';

import { Text } from '@change/ds-core-ui';

import { stripHtmlTags } from '@change-corgi/core/html';
import { memo } from '@change-corgi/core/react/core';
import { Translate } from '@change-corgi/core/react/i18n';

import type { PetitionContentUpdate } from 'src/app/pages/petitionDetails/shared/types';

type Props = Readonly<{
	update: PetitionContentUpdate;
}>;

function getVisibleText(el: HTMLElement): string {
	const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
	const textNode = walker.nextNode();
	if (!textNode || !textNode.textContent) return el.textContent ?? '';

	const fullText = textNode.textContent;
	const elBottom = el.getBoundingClientRect().bottom;
	const range = document.createRange();

	let lo = 0;
	let hi = fullText.length;

	while (lo < hi) {
		const mid = Math.floor((lo + hi + 1) / 2);
		range.setStart(textNode, 0);
		range.setEnd(textNode, mid);
		const rects = range.getClientRects();
		const lastRect = rects[rects.length - 1];
		if (lastRect && lastRect.bottom <= elBottom + 1) {
			lo = mid;
		} else {
			hi = mid - 1;
		}
	}

	return fullText.slice(0, lo) || fullText;
}

export const UpdateBody = memo<Props>(function UpdateBody({ update }): JSX.Element | null {
	const pRef = useRef<HTMLParagraphElement>(null);
	const [clamped, setClamped] = useState(false);
	const [visibleText, setVisibleText] = useState('');

	useEffect(() => {
		const el = pRef.current;
		if (!el) return;

		const isClamped = el.scrollHeight - el.clientHeight > 0;
		setClamped(isClamped);
		if (isClamped) {
			setVisibleText(getVisibleText(el));
		}
	}, [update.description]);

	if (!update.description) {
		return null;
	}

	const strippedDescription = stripHtmlTags(update.description);

	return (
		<div>
			{clamped && <span className="sr-only">{visibleText}&hellip;</span>}
			<Text
				as="div"
				ref={pRef}
				aria-hidden={clamped || undefined}
				className="line-clamp-2 break-normal wrap-anywhere italic"
				expressive
				color="typography-primary"
				variant="body"
				size="medium"
			>
				{strippedDescription}
			</Text>
			{clamped && (
				<Text className="text-accent-primary-500 mt-2 block text-sm underline">
					{update.type === 'dmResponse' ? (
						<Translate value="corgi.petition.details.updates.read_full_response" />
					) : (
						<Translate value="corgi.petition.details.updates.read_full_update" />
					)}
				</Text>
			)}
		</div>
	);
});
